import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Compass, Sparkles, Check } from "lucide-react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

const RATINGS = [
  { key: "significant", label: "Significant improvement", tone: "bg-[hsl(var(--neu-green))] text-white",   glyph: "✅" },
  { key: "some",        label: "Some improvement",        tone: "bg-[hsl(var(--neu-blue))] text-white",    glyph: "🙂" },
  { key: "none",        label: "No change",               tone: "bg-white/70 text-[hsl(var(--neu-ink))]",  glyph: "😐" },
  { key: "worse",       label: "Worse than before",       tone: "bg-[hsl(var(--neu-pink))]/15 text-[hsl(var(--neu-pink))]", glyph: "↩️" },
];

function StageShell({ title, subtitle, onBack, children }) {
  return (
    <div className="min-h-screen pb-24" data-testid="journey-screen">
      <div className="max-w-md mx-auto px-6 pt-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-white/70" data-testid="journey-back-btn">
            <ArrowLeft className="w-5 h-5"/>
          </button>
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-[hsl(var(--neu-orange))]"/>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Before ↔ After</div>
          </div>
        </div>
        <h1 className="font-display text-3xl leading-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-2 mb-6">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function AssessmentForm({ scenarios, initialResponses, cta, busy, onSubmit,
                          testIdPrefix }) {
  const [responses, setResponses] = useState(() => {
    const seed = {};
    scenarios.forEach((s) => {
      seed[s.id] = initialResponses?.[s.id] || "";
    });
    return seed;
  });
  const allFilled = scenarios.every((s) => (responses[s.id] || "").trim().length > 0);
  const submit = (e) => {
    e.preventDefault();
    if (!allFilled) { toast.error("Please write a short answer for all 3 scenarios."); return; }
    onSubmit(scenarios.map((s) => ({ scenario_id: s.id, response: responses[s.id].trim() })));
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      {scenarios.map((s, idx) => (
        <div key={s.id} className="card-soft p-5" data-testid={`${testIdPrefix}-scenario-${idx + 1}`}>
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--neu-orange))] mb-1">
            Scenario {idx + 1} of {scenarios.length}
          </div>
          <p className="font-medium leading-relaxed">{s.situation}</p>
          <div className="text-sm text-muted-foreground mt-3 mb-2">{s.prompt || "What would you do?"}</div>
          <textarea
            className="field min-h-[96px] resize-none"
            placeholder="Write what your child says (or paraphrase it in their voice)"
            value={responses[s.id] || ""}
            onChange={(e) => setResponses((r) => ({ ...r, [s.id]: e.target.value }))}
            data-testid={`${testIdPrefix}-response-${idx + 1}`}
          />
        </div>
      ))}
      <button disabled={busy || !allFilled} className="btn-primary w-full"
              data-testid={`${testIdPrefix}-submit`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>{cta} <ArrowRight className="w-4 h-4"/></>}
      </button>
    </form>
  );
}

function ReflectionForm({ scenarios, assessment, busy, onSubmit }) {
  const baseline = useMemo(() => {
    const m = {}; (assessment?.baseline || []).forEach((r) => { m[r.scenario_id] = r.response; }); return m;
  }, [assessment]);
  const final = useMemo(() => {
    const m = {}; (assessment?.final || []).forEach((r) => { m[r.scenario_id] = r.response; }); return m;
  }, [assessment]);
  const [reflections, setReflections] = useState({});
  const [notes, setNotes] = useState({});
  const allRated = scenarios.every((s) => !!reflections[s.id]);
  const submit = (e) => {
    e.preventDefault();
    if (!allRated) { toast.error("Please rate each scenario."); return; }
    onSubmit(scenarios.map((s) => ({
      scenario_id: s.id, rating: reflections[s.id], note: (notes[s.id] || "").trim(),
    })));
  };
  return (
    <form onSubmit={submit} className="space-y-6">
      {scenarios.map((s, idx) => (
        <div key={s.id} className="card-soft p-5" data-testid={`journey-reflect-scenario-${idx + 1}`}>
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--neu-orange))] mb-1">
            Scenario {idx + 1}
          </div>
          <p className="font-medium leading-relaxed">{s.situation}</p>
          <div className="grid grid-cols-1 gap-3 mt-4">
            <div className="rounded-2xl bg-white/60 border border-black/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Week 1 · Before</div>
              <p className="text-sm mt-1 whitespace-pre-wrap">{baseline[s.id] || <span className="text-muted-foreground italic">No response recorded.</span>}</p>
            </div>
            <div className="rounded-2xl bg-[hsl(var(--neu-orange))]/8 border border-[hsl(var(--neu-orange))]/25 p-3">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--neu-orange))]">Week 4 · After</div>
              <p className="text-sm mt-1 whitespace-pre-wrap">{final[s.id] || <span className="text-muted-foreground italic">No response recorded.</span>}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs text-muted-foreground mb-2">Did you notice an improvement?</div>
            <div className="flex flex-wrap gap-2">
              {RATINGS.map((r) => {
                const on = reflections[s.id] === r.key;
                return (
                  <button type="button" key={r.key}
                          onClick={() => setReflections((x) => ({ ...x, [s.id]: r.key }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            on ? r.tone + " border-transparent" : "bg-white/70 border-black/5 hover:bg-white"
                          }`}
                          data-testid={`journey-reflect-rating-${idx + 1}-${r.key}`}>
                    <span className="mr-1">{r.glyph}</span>{r.label}
                  </button>
                );
              })}
            </div>
          </div>
          <textarea
            className="field mt-3 min-h-[72px] resize-none text-sm"
            placeholder="What difference did you notice? (optional)"
            value={notes[s.id] || ""}
            onChange={(e) => setNotes((x) => ({ ...x, [s.id]: e.target.value }))}
            data-testid={`journey-reflect-note-${idx + 1}`}
          />
        </div>
      ))}
      <button disabled={busy || !allRated} className="btn-primary w-full"
              data-testid="journey-reflect-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Save reflection <ArrowRight className="w-4 h-4"/></>}
      </button>
    </form>
  );
}

function SummaryView({ scenarios, assessment, theme, childName, onDone }) {
  const baseline = useMemo(() => {
    const m = {}; (assessment?.baseline || []).forEach((r) => { m[r.scenario_id] = r.response; }); return m;
  }, [assessment]);
  const final = useMemo(() => {
    const m = {}; (assessment?.final || []).forEach((r) => { m[r.scenario_id] = r.response; }); return m;
  }, [assessment]);
  const refl = useMemo(() => {
    const m = {}; (assessment?.reflections || []).forEach((r) => { m[r.scenario_id] = r; }); return m;
  }, [assessment]);
  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-gradient-to-b from-[hsl(var(--neu-orange))]/15 to-transparent p-5 border border-[hsl(var(--neu-orange))]/20">
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--neu-orange))]">Journey complete</div>
        <div className="font-display text-2xl mt-1">{childName} · {theme?.title}</div>
        <p className="text-sm text-muted-foreground mt-2">
          Compare how {childName} thought before and after four weeks of meaningful family conversations.
        </p>
      </div>
      {scenarios.map((s, idx) => {
        const r = refl[s.id];
        const meta = r ? RATINGS.find((x) => x.key === r.rating) : null;
        return (
          <div key={s.id} className="card-soft p-5" data-testid={`journey-summary-scenario-${idx + 1}`}>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--neu-orange))] mb-1">
              Scenario {idx + 1}
            </div>
            <p className="font-medium leading-relaxed">{s.situation}</p>
            <div className="grid grid-cols-1 gap-3 mt-4">
              <div className="rounded-2xl bg-white/60 border border-black/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Before</div>
                <p className="text-sm mt-1 whitespace-pre-wrap">{baseline[s.id] || "—"}</p>
              </div>
              <div className="rounded-2xl bg-[hsl(var(--neu-orange))]/8 border border-[hsl(var(--neu-orange))]/25 p-3">
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--neu-orange))]">After</div>
                <p className="text-sm mt-1 whitespace-pre-wrap">{final[s.id] || "—"}</p>
              </div>
            </div>
            {r && (
              <div className="mt-3">
                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${meta?.tone || "bg-white/60"}`}>
                  <span>{meta?.glyph}</span> {meta?.label || r.rating}
                </div>
                {r.note && (
                  <p className="text-xs text-muted-foreground mt-2 italic">&ldquo;{r.note}&rdquo;</p>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="card-soft p-5 text-center bg-[hsl(var(--neu-green))]/8 border border-[hsl(var(--neu-green))]/25">
        <Sparkles className="w-6 h-6 text-[hsl(var(--neu-green))] mx-auto mb-2"/>
        <div className="font-display text-lg">
          {childName} has completed the {theme?.title} Journey.
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Small conversations today. Lifelong impact tomorrow.
        </p>
      </div>
      <button onClick={onDone} className="btn-primary w-full" data-testid="journey-summary-done">
        <Check className="w-4 h-4"/> Back to home
      </button>
    </div>
  );
}

export default function Journey() {
  const nav = useNavigate();
  const { me } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/session/journey/state");
      setState(r.data);
    } catch (e) {
      toast.error("Could not load journey");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitBaseline = async (responses) => {
    setBusy(true);
    try {
      await api.post("/session/journey/baseline", { theme_id: state.theme.id, responses });
      toast.success("Baseline saved. Enjoy Week 1!");
      nav("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const submitFinal = async (responses) => {
    setBusy(true);
    try {
      await api.post("/session/journey/final", { theme_id: state.theme.id, responses });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const submitReflect = async (reflections) => {
    setBusy(true);
    try {
      await api.post("/session/journey/reflect", { theme_id: state.theme.id, reflections });
      await load();
      toast.success("Journey reflection saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!state || !state.theme) {
    return (
      <StageShell title="Nothing to compare yet" onBack={() => nav("/home")}>
        <p className="text-muted-foreground">
          Your journey will appear here once your child begins their first monthly theme.
        </p>
      </StageShell>
    );
  }
  if (state.stage === "unavailable") {
    return (
      <StageShell title="Journey check-ins coming soon"
                   subtitle="We're preparing the baseline scenarios for this theme."
                   onBack={() => nav("/home")}>
        <p className="text-muted-foreground text-sm">
          You can still start this week&apos;s session from the home screen.
        </p>
      </StageShell>
    );
  }
  if (state.stage === "in_progress") {
    return (
      <StageShell title="Journey in progress"
                   subtitle="You'll be prompted again after Week 4."
                   onBack={() => nav("/home")}>
        <div className="text-sm text-muted-foreground">
          Weeks completed in this theme: <b>{state.weeks_completed_in_theme || 0}</b> of {state.total_weeks_in_theme || 4}.
        </div>
      </StageShell>
    );
  }

  const child = me?.child?.name || "your child";

  if (state.stage === "baseline_required") {
    return (
      <StageShell title={`Baseline check-in · ${state.theme.title}`}
                   subtitle={`Before Week 1 begins, ask ${child} how they would respond to these 3 short scenarios.`}
                   onBack={() => nav("/home")}>
        <AssessmentForm scenarios={state.scenarios} cta="Save baseline"
                         busy={busy} onSubmit={submitBaseline}
                         testIdPrefix="journey-baseline"/>
      </StageShell>
    );
  }
  if (state.stage === "final_required") {
    return (
      <StageShell title={`Final check-in · ${state.theme.title}`}
                   subtitle={`Four weeks in — ask ${child} the same 3 scenarios to see how their thinking has shifted.`}
                   onBack={() => nav("/home")}>
        <AssessmentForm scenarios={state.scenarios} cta="Save final"
                         busy={busy} onSubmit={submitFinal}
                         testIdPrefix="journey-final"/>
      </StageShell>
    );
  }
  if (state.stage === "reflection_required") {
    return (
      <StageShell title="Compare before ↔ after"
                   subtitle="For each scenario, did you notice a shift?"
                   onBack={() => nav("/home")}>
        <ReflectionForm scenarios={state.scenarios} assessment={state.assessment}
                         busy={busy} onSubmit={submitReflect}/>
      </StageShell>
    );
  }
  if (state.stage === "summary") {
    return (
      <StageShell title="Journey summary" onBack={() => nav("/home")}>
        <SummaryView scenarios={state.scenarios} assessment={state.assessment}
                      theme={state.theme} childName={child}
                      onDone={() => nav("/home")}/>
      </StageShell>
    );
  }
  return null;
}
